import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Which sidebar tab is open, kept in the URL instead of in component state.
 *
 * WHY IT IS NOT useState. The shells are one route each — the tab is not part of the path — so a
 * reload had nothing to restore from and always dropped you back on whatever the default was. That
 * is fine for a screen you glance at and wrong for one you work in: a hard refresh while reading
 * Findings sent you to the Dashboard.
 *
 * `?tab=` also makes a tab linkable, which is the thing you want the moment somebody needs to send
 * a colleague to the Standards Library rather than to "the module, then click the third item".
 *
 * REPLACE, NOT PUSH. Moving between tabs does not stack history: a sidebar is navigation within a
 * screen, and pushing would make Back walk every tab you had visited before leaving the module.
 * The refresh behaviour is the same either way.
 *
 * An unknown or missing tab falls back to the default rather than rendering nothing — a link with a
 * stale key in it should land somewhere sensible, not on a blank panel.
 *
 * CHOOSING A TAB ALSO CLOSES WHATEVER WAS OPEN INSIDE ONE. A sidebar item names a SECTION, and
 * pressing it means "take me to that section" — not "take me to the third screen down inside it,
 * where I was". Pressing "Admin Console" while inside a project used to do nothing at all, because
 * the tab was already `projects` and the open project was state the sidebar could not see. Deeper
 * position lives in its own parameter (see NESTED_PARAMS) and is dropped here.
 */
/** Parameters that describe a position INSIDE a tab, and so do not survive leaving it. */
const NESTED_PARAMS = ["project", "standard"];
export function useNavTab(
  defaultKey: string,
  valid: readonly string[],
): [string, (key: string) => void] {
  const [params, setParams] = useSearchParams();

  const requested = params.get("tab");
  const active = requested && valid.includes(requested) ? requested : defaultKey;

  const setActive = useCallback(
    (key: string) => {
      const next = new URLSearchParams(params);
      next.set("tab", key);
      for (const nested of NESTED_PARAMS) next.delete(nested);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  return [active, setActive];
}
